import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {StyledHomeCard} from './style'
import {useTranslation} from 'react-i18next'
import {HomeReportsSkeleton} from './HomeReportsSkeleton'
import {useGetHomePageData} from '../../query/useGetHomePageData'
import {InfoCircleIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {medicalReportKeyMapToLabel, generateCreateMedicalReportPath} from '@/features/medical-reports/utils'
import {formatLocaleDate} from '@/dayjs'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {generatePath} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {useState} from 'react'
import dayjs from 'dayjs'
import {DateRangeFilter} from '@/components/commons/date-range-filter/DateRangeFilter'
import {DateRangeFilterEnum} from '@/types'

type ReportsDateRange = {range: DateRangeFilterEnum; startDate: string; endDate: string}

// Reports are scoped to the current year by default; the user can change the period from the card.
const getDefaultReportsDateRange = (): ReportsDateRange => ({
    range: 'year',
    startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('year').format('YYYY-MM-DD')
})

export const HomeReportCard = () => {
    const {t} = useTranslation()
    const [dateRange, setDateRange] = useState<ReportsDateRange>(getDefaultReportsDateRange)
    const {data, isLoading} = useGetHomePageData({
        fields: ['reports'],
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
    })

    return (
        <StyledHomeCard>
            <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                <Text size="textMd" weight={500} palette="neutral" shade="600">
                    {t('homepage:reports_to_complete')}
                </Text>
                <DateRangeFilter size="sm" values={dateRange} onApply={setDateRange} />
            </Flexbox>
            {isLoading ? (
                <HomeReportsSkeleton />
            ) : (
                <>
                    <Text size="displayMd" palette="neutral" shade="900" weight={700}>
                        {(data?.reports?.missed?.length ?? 0) + (data?.reports?.draft?.length ?? 0)}
                    </Text>
                    <Flexbox direction="column" gap={2}>
                        <Flexbox gap={2}>
                            <Text weight={500} size="textSm" palette="neutral" shade="950">
                                {data?.reports?.missed?.length}
                            </Text>
                            <Text size="textSm" palette="neutral" shade="600">
                                {t('homepage:missing_reports')}
                            </Text>
                            <Tooltip
                                variant="light"
                                content={
                                    <Flexbox direction="column" gap={1}>
                                        {data?.reports?.missed.slice(0, 10).map(access => (
                                            <Flexbox key={access.id} gap={1}>
                                                <Text size="textSm" palette="neutral" shade="600">
                                                    {formatLocaleDate(access.event.date, 'DD/MM/YYYY')}
                                                </Text>
                                                {access.event.eventConsultants?.[0]?.accessType?.medicalRecordReportType
                                                    ?.keyName && (
                                                    <ButtonLink
                                                        variant="tertiary"
                                                        size="sm"
                                                        shape="text"
                                                        to={
                                                            generateCreateMedicalReportPath(
                                                                access.patientId,
                                                                access.projectId,
                                                                access.event?.eventConsultants?.[0]?.accessType
                                                                    ?.medicalRecordReportType,
                                                                [access.id]
                                                            ) ?? ''
                                                        }
                                                    >
                                                        {t(
                                                            medicalReportKeyMapToLabel[
                                                                access.event.eventConsultants?.[0]?.accessType
                                                                    ?.medicalRecordReportType?.keyName
                                                            ]
                                                        )}
                                                    </ButtonLink>
                                                )}
                                            </Flexbox>
                                        ))}
                                    </Flexbox>
                                }
                            >
                                <Button variant="ghost" size="sm">
                                    <InfoCircleIcon size={16} />
                                </Button>
                            </Tooltip>
                        </Flexbox>
                        <Flexbox gap={2} align="center">
                            <Text weight={500} size="textSm" palette="neutral" shade="950">
                                {data?.reports?.draft?.length}
                            </Text>
                            <Text size="textSm" palette="neutral" shade="600">
                                {t('homepage:draft_report')}
                            </Text>
                            <Tooltip
                                variant="light"
                                content={
                                    <Flexbox direction="column" gap={1}>
                                        {data?.reports?.draft?.slice(0, 10).map(report => (
                                            <Flexbox key={report.id} direction="column" gap={1}>
                                                <Flexbox align="center" gap={1}>
                                                    <Text size="textSm" palette="neutral" shade="600">
                                                        {formatLocaleDate(report.createdAt, 'DD/MM/YYYY')}
                                                    </Text>
                                                    <ButtonLink
                                                        variant="tertiary"
                                                        size="sm"
                                                        shape="text"
                                                        to={generatePath(routes.EDIT_MEDICAL_REPORT.handle.path, {
                                                            id: report.project.patientId,
                                                            medicalReportId: report.id
                                                        })}
                                                    >
                                                        {t(
                                                            medicalReportKeyMapToLabel[
                                                                report.medicalRecordReportType.keyName
                                                            ]
                                                        )}
                                                    </ButtonLink>
                                                </Flexbox>
                                            </Flexbox>
                                        ))}
                                    </Flexbox>
                                }
                            >
                                <Button variant="ghost" size="sm">
                                    <InfoCircleIcon size={16} />
                                </Button>
                            </Tooltip>
                        </Flexbox>
                    </Flexbox>
                </>
            )}
        </StyledHomeCard>
    )
}
